// The single source of truth for "what does this secret render as
// when displayed in surface X?".
//
// Property 9 (Req 10.10, 14.5):
//   Surfaces that could leak Secrets content render only the
//   placeholder.
//
// We model the decision as a pure function on:
//
//   - the message kind (`secret` is the dangerous case)
//   - the surface kind (where the message is being rendered)
//   - whether the surface is the *active* in-channel cell with the
//     user's explicit click-to-reveal state (the only place where
//     ciphertext content may surface)
//
// Every surface in the app must call this function rather than
// substituting its own fallback. The property test in
// `secrets-placeholder.test.ts` enforces that for any randomly-
// generated secret body and surface, only `surface === "channel"
// && reveal === true` ever returns the body — every other call
// returns the placeholder string.
//
// This is the kind of test where it pays to be a little paranoid:
// we never want to introduce a new surface (Notes, Search, Mention
// preview, channel-list preview, push notifications, …) that
// accidentally renders the body. Routing every call through here
// gives us one place to audit.

/** Surfaces that ever display message content. */
export type RenderSurface =
  /** The actual message row inside a channel. */
  | "channel"
  /** Right-rail Notes view (pinned messages). */
  | "notes"
  /** Search results. */
  | "search"
  /** A `@user said: …` mention preview. */
  | "mention"
  /** Channel-list "last message" preview line. */
  | "channel-preview"
  /** Browser / OS notification. */
  | "notification";

/** Message kinds known to the renderer. */
export type RenderableKind = "text" | "snippet" | "file" | "secret";

export interface PlaceholderInput {
  kind: RenderableKind;
  surface: RenderSurface;
  /** Verbatim message body. Never substituted unless we explicitly return it. */
  body: string;
  /**
   * The user's explicit click-to-reveal state for this row. Only
   * meaningful for `surface === "channel"`. Every other surface
   * MUST ignore this even if a caller mistakenly passes `true`.
   */
  revealed?: boolean;
}

/** What every consumer renders, post-policy. */
export interface PlaceholderOutput {
  /** The string the surface should display. */
  display: string;
  /** True when `display` is the redacted placeholder, not the body. */
  redacted: boolean;
}

export const SECRET_PLACEHOLDER = "(secret)";

/**
 * The single placeholder decision. `display` is what the surface
 * should render; `redacted` is true whenever we substituted the
 * placeholder for the real body.
 */
export function renderForSurface(input: PlaceholderInput): PlaceholderOutput {
  if (input.kind !== "secret") {
    return { display: input.body, redacted: false };
  }

  // For secrets, only the in-channel surface with an explicit
  // reveal may show the actual body. Every other surface — and
  // the channel surface without reveal — gets the placeholder.
  if (input.surface === "channel" && input.revealed === true) {
    return { display: input.body, redacted: false };
  }
  return { display: SECRET_PLACEHOLDER, redacted: true };
}
